import { supabase } from '@/integrations/supabase/client';
import type { 
  Game, 
  GameWithDetails, 
  CreateGameData, 
  GameParticipant, 
  GameConfirmation,
  GameResult,
  UpdateParticipantStatusData,
  LeaderboardEntry 
} from '@/types/gameSystem';

export const gameManagementService = {
  // Game CRUD operations
  async createGame(gameData: CreateGameData, createdBy: string, userType: 'supabase' | 'internal' = 'supabase'): Promise<Game> {
    console.log('🎮 Service createGame - Starting game creation process');
    console.log('🎮 Service createGame - provided userId:', createdBy);
    console.log('🎮 Service createGame - userType:', userType);
    
    // Only perform Supabase auth check for Supabase users
    if (userType === 'supabase') {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      
      console.log('🎮 Service createGame - auth check result:', { user: user?.id, authError });
      
      if (!user || !user.id) {
        console.error('🎮 Service createGame - Supabase authentication failed:', { user, authError });
        throw new Error('Authentication session expired. Please refresh the page and try again.');
      }

      if (user.id !== createdBy) {
        console.error('🎮 Service createGame - User ID mismatch:', { authUserId: user.id, providedUserId: createdBy });
        throw new Error('Authentication mismatch. Please refresh the page and try again.');
      }

      console.log('🎮 Service createGame - Supabase auth verified successfully');
    } else {
      console.log('🎮 Service createGame - Internal user, skipping Supabase auth check');
    }

    console.log('🎮 Service createGame - Using userId:', createdBy);

    // Ensure required fields are present
    if (!gameData.game_name?.trim()) {
      throw new Error('Game name is required');
    }
    if (!gameData.game_date) {
      throw new Error('Game date is required');
    }
    if (!gameData.game_time) {
      throw new Error('Game time is required');
    }
    if (!gameData.sport_id) {
      throw new Error('Sport selection is required');
    }
    if (!gameData.age_group) {
      throw new Error('Age group selection is required');
    }

    // Prepare the game data with proper defaults and validation
    const gameInsertData = {
      game_name: gameData.game_name.trim(),
      game_date: gameData.game_date,
      game_time: gameData.game_time,
      game_timezone: gameData.game_timezone || 'UTC',
      venue_id: gameData.venue_id || null,
      venue_name: gameData.venue_name || gameData.field_location || 'TBD',
      venue_address: gameData.venue_address || 'TBD',
      sport_id: gameData.sport_id,
      age_group: gameData.age_group,
      home_team_id: gameData.home_team_id || null,
      away_team_id: gameData.away_team_id || null,
      home_team_label: gameData.home_team_label || 'Home',
      away_team_label: gameData.away_team_label || 'Away',
      minimum_players_required: gameData.minimum_players_required || 5,
      created_by: createdBy, // Use provided userId (validated by auth check for Supabase users)
      game_status: 'pending',
      confirmation_status: 'pending'
    };

    console.log('🎮 Inserting game data:', gameInsertData);

    const { data, error } = await supabase
      .from('games')
      .insert(gameInsertData)
      .select()
      .single();

    console.log('🎮 Database insert response:', { data, error });

    if (error) {
      console.error('🎮 Database error creating game:', error);
      
      // Provide more specific error messages
      if (error.code === '23505') {
        throw new Error('A game with these details already exists. Please modify your game details.');
      } else if (error.code === '23503') {
        throw new Error('Invalid team or venue selection. Please check your selections.');
      } else if (error.message?.includes('auth')) {
        throw new Error('Authentication error. Please refresh the page and try again.');
      } else {
        throw new Error(`Failed to create game: ${error.message || 'Unknown database error'}`);
      }
    }

    if (!data) {
      console.error('🎮 No data returned from database insert');
      throw new Error('Game creation failed - no data returned. Please try again.');
    }

    console.log('🎮 Game created successfully:', data);

    // Track game creation activity (fire-and-forget)
    import('./activityTrackingService').then(({ activityTrackingService }) => {
      const title = gameData.home_team_label && gameData.away_team_label
        ? `Created game: ${gameData.home_team_label} vs ${gameData.away_team_label}`
        : `Created a new game`;
      activityTrackingService.recordActivity({
        userId: createdBy,
        userType: 'supabase',
        activityType: 'game_created',
        activityTitle: title,
        activityDescription: gameData.venue_name ? `At ${gameData.venue_name}` : undefined,
        activityData: { gameName: gameData.game_name, gameDate: gameData.game_date, venueName: gameData.venue_name },
        gameId: data.id,
        isPublic: true
      });
    }).catch(console.error);

    return data as Game;
  },

  async getGame(gameId: string): Promise<GameWithDetails | null> {
    try {
      const { data, error } = await supabase
        .from('games')
        .select(`
          *,
          home_team:teams!games_home_team_id_fkey(team_id, team_name, team_logo),
          away_team:teams!games_away_team_id_fkey(team_id, team_name, team_logo),
          sport:available_sports(id, sport_name)
        `)
        .eq('id', gameId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') return null;
        console.error('Error fetching game:', error);
        return null;
      }

      // Get participants, confirmations, and rosters
      const [participantsData, confirmationsData, resultData, rostersData] = await Promise.all([
        this.getGameParticipants(gameId),
        this.getGameConfirmations(gameId),
        this.getGameResult(gameId),
        this.getGameRosters(gameId)
      ]);

      return {
        ...data,
        participants: participantsData,
        confirmations: confirmationsData,
        result: resultData,
        rosters: rostersData
      } as GameWithDetails;
    } catch (error) {
      console.error('Error getting game details:', error);
      return null;
    }
  },

  async updateGame(gameId: string, updates: Partial<Game>): Promise<Game> {
    console.log('🎮 GAME SERVICE - Updating game:', { gameId, updates });
    
    try {
      const { data, error } = await supabase
        .from('games')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', gameId)
        .select(`
          *,
          home_team:teams!games_home_team_id_fkey(team_id, team_name, team_logo),
          away_team:teams!games_away_team_id_fkey(team_id, team_name, team_logo),
          sport:available_sports(id, sport_name)
        `)
        .single();

      if (error) {
        console.error('🎮 GAME SERVICE - Update error:', error);
        throw error;
      }

      console.log('🎮 GAME SERVICE - Game updated successfully:', data);
      return data as Game;
    } catch (error) {
      console.error('🎮 GAME SERVICE - Update failed:', error);
      throw error;
    }
  },

  async deleteGame(gameId: string): Promise<void> {
    const { error } = await supabase
      .from('games')
      .delete()
      .eq('id', gameId);

    if (error) throw error;
  },

  // Game listing functions
  async getGamesForUser(userId: string, userType: 'supabase' | 'internal' = 'supabase'): Promise<GameWithDetails[]> {
    console.log('🎮 Loading games for user:', { userId, userType });
    
    try {
      let allGames: any[] = [];

      // ALWAYS include games created by the user (covers unified form games)
      console.log('📋 Fetching games created by user...');
      const { data: createdGames, error: createdGamesError } = await supabase
        .from('games')
        .select(`
          *,
          home_team:teams!games_home_team_id_fkey(team_id, team_name, team_logo),
          away_team:teams!games_away_team_id_fkey(team_id, team_name, team_logo),
          sport:available_sports(id, sport_name),
          participants:game_participants(id, player_id, team_side, participation_status, eligibility_status),
          confirmations:game_confirmations(id, team_side, confirmation_status, confirmed_at),
          rosters:game_rosters(id, player_id, team_side, is_eligible)
        `)
        .eq('created_by', userId)
        .order('game_date', { ascending: true });

      if (createdGamesError) {
        console.error('❌ Error fetching created games:', createdGamesError);
      } else if (createdGames) {
        allGames.push(...createdGames);
        console.log('✅ Found games created by user:', createdGames.length);
        console.log('🎮 Created games details:', createdGames.map(g => ({ 
          id: g.id, 
          name: g.game_name, 
          date: g.game_date, 
          status: g.game_status 
        })));
      } else {
        console.log('📝 No games created by user found');
      }

      // Get the user's team associations for additional games
      const teamIds = await this.getUserTeamIds(userId);
      const teamIdsArray = teamIds.split(',').filter(id => id !== '0').map(id => parseInt(id));
      console.log('🏀 User team IDs array:', teamIdsArray);

      // If user has team associations, also include team games
      if (teamIdsArray.length > 0) {
        console.log('📋 Fetching team games for teams:', teamIdsArray);
        const { data: teamGames, error: teamGamesError } = await supabase
          .from('games')
          .select(`
            *,
            home_team:teams!games_home_team_id_fkey(team_id, team_name, team_logo),
            away_team:teams!games_away_team_id_fkey(team_id, team_name, team_logo),
            sport:available_sports(id, sport_name),
            participants:game_participants(id, player_id, team_side, participation_status, eligibility_status),
            confirmations:game_confirmations(id, team_side, confirmation_status, confirmed_at),
            rosters:game_rosters(id, player_id, team_side, is_eligible)
          `)
          .or(`home_team_id.in.(${teamIdsArray.join(',')}),away_team_id.in.(${teamIdsArray.join(',')})`)
          .neq('created_by', userId) // Avoid duplicates with created games
          .order('game_date', { ascending: true });

        if (teamGamesError) {
          console.error('❌ Error fetching team games:', teamGamesError);
        } else if (teamGames) {
          allGames.push(...teamGames);
          console.log('✅ Found team games:', teamGames.length, 'for teams:', teamIdsArray);
        } else {
          console.log('📝 No team games found for teams:', teamIdsArray);
        }
      } else {
        console.log('🏀 User has no team associations, skipping team games');
      }

      // Check venue games for venue managers and team associations
      console.log('📋 Fetching venue games...');
      let venueGameQuery = supabase
        .from('venue_games')
        .select(`
          *,
          sport:available_sports(id, sport_name)
        `)
        .order('game_date', { ascending: true });

      // Show venue games where user is venue manager OR user's teams are playing
      const venueConditions = [`venue_id.eq.${userId}`];
      if (teamIdsArray.length > 0) {
        venueConditions.push(`home_team_id.in.(${teamIdsArray.join(',')})`);
        venueConditions.push(`away_team_id.in.(${teamIdsArray.join(',')})`);
      }

      const { data: venueGamesData, error: venueError } = await venueGameQuery
        .or(venueConditions.join(','));

      if (venueError) {
        console.error('❌ Error fetching venue games:', venueError);
      } else if (venueGamesData) {
        // Transform venue games to match the games table structure
        const transformedVenueGames = await Promise.all(
          venueGamesData.map(async (vg: any) => {
            // Fetch participants for this venue game
            const { data: participants } = await supabase
              .from('game_participants')
              .select('id, player_id, team_side, participation_status, eligibility_status')
              .eq('game_id', vg.id);

            // Fetch rosters for this venue game
            const { data: rosters } = await supabase
              .from('game_rosters')
              .select('id, player_id, team_side, is_eligible')
              .eq('game_id', vg.id);

            // Fetch team data if team IDs exist
            let homeTeam = null;
            let awayTeam = null;
            
            if (vg.home_team_id) {
              const { data: homeTeamData } = await supabase
                .from('teams')
                .select('team_id, team_name, team_logo')
                .eq('team_id', vg.home_team_id)
                .maybeSingle();
              homeTeam = homeTeamData;
            }
            
            if (vg.away_team_id) {
              const { data: awayTeamData } = await supabase
                .from('teams')
                .select('team_id, team_name, team_logo')
                .eq('team_id', vg.away_team_id)
                .maybeSingle();
              awayTeam = awayTeamData;
            }

            // Get venue name - use field_location from venue_games or default
            const venueName = vg.field_location || 'TBD';

            return {
              id: vg.id,
              game_name: vg.game_title,
              game_date: vg.game_date,
              game_time: vg.game_time,
              venue_id: vg.venue_id,
              venue_name: venueName,
              venue_address: 'TBD',
              sport_id: vg.sport_id || 1, // Fallback to prevent null
              home_team_id: vg.home_team_id,
              away_team_id: vg.away_team_id,
              home_team: homeTeam,
              away_team: awayTeam,
              home_team_label: 'Home',
              away_team_label: 'Away',
              game_status: vg.game_status === 'approved' ? 'confirmed' : vg.game_status,
              confirmation_status: vg.status === 'approved' ? 'confirmed' : 'pending',
              game_code: vg.game_code,
              created_by: vg.posted_by || vg.venue_id,
              created_at: vg.created_at,
              updated_at: vg.updated_at,
              game_timezone: 'UTC',
              participants: participants || [],
              confirmations: [],
              rosters: rosters || [],
              sport: vg.sport || { id: vg.sport_id || 1, sport_name: 'Basketball' },
              minimum_players_required: 4, // Default value since venue_games table might not have this field
              scorekeeper_needed: vg.scorekeeper_needed || false,
              scorekeeper_points: vg.scorekeeper_points || 0,
              scorekeeper_assigned: vg.scorekeeper_assigned,
              source: 'venue'
            };
          })
        );
        
        allGames.push(...transformedVenueGames);
        console.log('✅ Found venue games:', transformedVenueGames.length);
      } else {
        console.log('📝 No venue games found');
      }

      // Remove duplicates based on ID and sort by date
      const uniqueGames = allGames.filter((game, index, arr) => 
        arr.findIndex(g => g.id === game.id) === index
      ).sort((a, b) => new Date(a.game_date).getTime() - new Date(b.game_date).getTime());
      
      console.log('✅ Successfully loaded games:', uniqueGames.length);
      console.log('🎮 Game sources breakdown:', {
        total: uniqueGames.length,
        venue: uniqueGames.filter(g => g.source === 'venue').length,
        regular: uniqueGames.filter(g => !g.source || g.source !== 'venue').length
      });
      
      return uniqueGames as any[];
    } catch (error) {
      console.error('❌ Error in getGamesForUser:', error);
      throw error;
    }
  },

  async getUpcomingGames(limit?: number): Promise<GameWithDetails[]> {
    let query = supabase
      .from('games')
      .select(`
        *,
        home_team:teams!games_home_team_id_fkey(team_id, team_name, team_logo),
        away_team:teams!games_away_team_id_fkey(team_id, team_name, team_logo),
        sport:available_sports(id, sport_name)
      `)
      .gte('game_date', new Date().toISOString().split('T')[0])
      .order('game_date', { ascending: true });

    if (limit) {
      query = query.limit(limit);
    }

    const { data, error } = await query;
    if (error) throw error;
    return (data || []) as any[];
  },

  // Game participants management
  async addParticipant(gameId: string, participantData: Omit<GameParticipant, 'id' | 'game_id' | 'created_at' | 'updated_at' | 'status_updated_at'>): Promise<GameParticipant> {
    const { data, error } = await supabase
      .from('game_participants')
      .insert({
        game_id: gameId,
        ...participantData
      })
      .select()
      .single();

    if (error) throw error;
    return data as GameParticipant;
  },

  async updateParticipantStatus(participantId: string, updates: UpdateParticipantStatusData): Promise<GameParticipant> {
    const { data, error } = await supabase
      .from('game_participants')
      .update({
        ...updates,
        status_updated_at: new Date().toISOString()
      })
      .eq('id', participantId)
      .select()
      .single();

    if (error) throw error;
    return data as GameParticipant;
  },

  async removeParticipant(participantId: string): Promise<void> {
    const { error } = await supabase
      .from('game_participants')
      .delete()
      .eq('id', participantId);

    if (error) throw error;
  },

  async getGameParticipants(gameId: string): Promise<GameParticipant[]> {
    const { data, error } = await supabase
      .from('game_participants')
      .select('*')
      .eq('game_id', gameId)
      .order('team_side', { ascending: true });

    if (error) throw error;
    return (data || []) as GameParticipant[];
  },

  async getGameRosters(gameId: string): Promise<any[]> {
    const { data, error } = await supabase
      .from('game_rosters')
      .select('*')
      .eq('game_id', gameId)
      .order('team_side', { ascending: true });

    if (error) throw error;
    return data || [];
  },

  // Game confirmations
  async confirmGame(gameId: string, teamSide: 'home' | 'away', teamId: number, confirmedBy: string, confirmedByType: 'supabase' | 'internal' = 'supabase'): Promise<GameConfirmation> {
    console.log('🎯 Confirming game:', { gameId, teamSide, teamId, confirmedBy, confirmedByType });
    
    // First check if this is a venue game or regular game
    const { data: venueGame } = await supabase
      .from('venue_games')
      .select('id')
      .eq('id', gameId)
      .single();

    if (venueGame) {
      console.log('📍 Venue game found, using venue_game_confirmations');
      
      // For venue games, use venue_game_confirmations and update ready status
      const { data, error } = await supabase
        .from('venue_game_confirmations')
        .upsert({
          game_id: gameId,
          team_side: teamSide,
          team_id: teamId,
          confirmed_by: confirmedBy,
          confirmed_by_type: confirmedByType,
          confirmation_status: true,
          confirmed_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) {
        console.error('❌ Error inserting venue_game_confirmation:', error);
        throw error;
      }

      console.log('✅ Venue game confirmation created:', data);

      // Also update the ready status in venue_games table
      const readyField = teamSide === 'home' ? 'home_team_ready' : 'away_team_ready';
      const { error: updateError } = await supabase
        .from('venue_games')
        .update({ [readyField]: true })
        .eq('id', gameId);

      if (updateError) {
        console.error('❌ Error updating team ready status:', updateError);
        throw updateError;
      }

      console.log(`✅ Updated ${readyField} to true for game ${gameId}`);
      
      // Don't show additional confirmation toast here - it's shown from the UI
      return data as GameConfirmation;
    } else {
      // For regular games, use game_confirmations
      const { data, error } = await supabase
        .from('game_confirmations')
        .upsert({
          game_id: gameId,
          team_side: teamSide,
          team_id: teamId,
          confirmed_by: confirmedBy,
          confirmed_by_type: confirmedByType,
          confirmation_status: true,
          confirmed_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) throw error;
      return data as GameConfirmation;
    }
  },

  async getGameConfirmations(gameId: string): Promise<GameConfirmation[]> {
    const { data, error } = await supabase
      .from('game_confirmations')
      .select('*')
      .eq('game_id', gameId);

    if (error) throw error;
    return (data || []) as GameConfirmation[];
  },

  // Game results
  async recordGameResult(gameId: string, resultData: Omit<GameResult, 'id' | 'game_id' | 'created_at' | 'updated_at'>): Promise<GameResult> {
    const { data, error } = await supabase
      .from('game_results')
      .upsert({
        game_id: gameId,
        ...resultData
      })
      .select()
      .single();

    if (error) throw error;
    return data as GameResult;
  },

  async getGameResult(gameId: string): Promise<GameResult | null> {
    const { data, error } = await supabase
      .from('game_results')
      .select('*')
      .eq('game_id', gameId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }

    return data as GameResult;
  },

  // Leaderboard functions
  async getLeaderboard(entityType?: 'player' | 'coach' | 'team', sportId?: number, seasonYear?: number): Promise<LeaderboardEntry[]> {
    let query = supabase
      .from('leaderboard')
      .select('*')
      .order('win_rate', { ascending: false })
      .order('games_won', { ascending: false });

    if (entityType) {
      query = query.eq('entity_type', entityType);
    }

    if (sportId) {
      query = query.eq('sport_id', sportId);
    }

    if (seasonYear) {
      query = query.eq('season_year', seasonYear);
    } else {
      query = query.eq('season_year', new Date().getFullYear());
    }

    const { data, error } = await query;
    if (error) throw error;
    return (data || []) as LeaderboardEntry[];
  },

  async getEntityStats(entityId: string, entityType: 'player' | 'coach' | 'team', entitySubtype: 'supabase' | 'internal' = 'supabase'): Promise<LeaderboardEntry | null> {
    const { data, error } = await supabase
      .from('leaderboard')
      .select('*')
      .eq('entity_id', entityId)
      .eq('entity_type', entityType)
      .eq('entity_subtype', entitySubtype)
      .eq('season_year', new Date().getFullYear())
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }

    return data as LeaderboardEntry;
  },

  // Helper functions
  async getUserTeamIds(userId: string): Promise<string> {
    try {
      // Fix the .or() syntax and add better error handling
      const { data: teams, error: teamsError } = await supabase
        .from('teams')
        .select('team_id')
        .eq('created_by', userId)
        .limit(100);

      if (teamsError) {
        console.error('❌ Error fetching user teams:', teamsError);
      }

      const { data: coachTeams, error: coachError } = await supabase
        .from('team_coaches')
        .select('team_id')
        .eq('coach_id', userId)
        .limit(100);

      if (coachError) {
        console.error('❌ Error fetching coach teams:', coachError);
      }

      const teamIds = new Set<number>();
      teams?.forEach(t => teamIds.add(t.team_id));
      coachTeams?.forEach(t => teamIds.add(t.team_id));

      const result = Array.from(teamIds).join(',') || '0';
      console.log('🏀 Team IDs found for user:', { userId, teamIds: Array.from(teamIds), result });
      return result;
    } catch (error) {
      console.error('❌ Fatal error in getUserTeamIds:', error);
      return '0';
    }
  },

  // Real-time countdown calculation
  calculateTimeRemaining(countdownEndAt: string): {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
    total_seconds: number;
    expired: boolean;
  } {
    const now = new Date().getTime();
    const end = new Date(countdownEndAt).getTime();
    const diff = end - now;

    if (diff <= 0) {
      return {
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
        total_seconds: 0,
        expired: true
      };
    }

    const total_seconds = Math.floor(diff / 1000);
    const days = Math.floor(total_seconds / (24 * 60 * 60));
    const hours = Math.floor((total_seconds % (24 * 60 * 60)) / (60 * 60));
    const minutes = Math.floor((total_seconds % (60 * 60)) / 60);
    const seconds = total_seconds % 60;

    return {
      days,
      hours,
      minutes,
      seconds,
      total_seconds,
      expired: false
    };
  }
};